// src/services/publicService.ts
// שירותים ציבוריים שלא דורשים אימות

import { API_BASE_URL } from '../../config/api';

export interface CalculationCounterResponse {
  res: string;
  total_count: number;
}

/**
 * Get total calculation counter (public API - no auth required)
 */
export const getCalculationCounter = async (): Promise<CalculationCounterResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/Api/service/enduser/get_calculation_counter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch calculation counter');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching calculation counter:', error);
    // Return default value on error
    return { res: 'OK', total_count: 6892 };
  }
};
// ───── מאמרים ─────

export interface ArticleCategory {
  id: number;
  name: string;
  slug: string;
  icon: string;
  sort_order: number;
  article_count: number;
}

export interface Article {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  cover_image_url?: string | null;
  read_time_minutes: number;
  published_at: string | null;
  category_id: number;
  category_name: string;
  category_slug: string;
}

export interface ArticleFull extends Article {
  content: string;
  meta_description: string;
}

export interface ArticlesPagination {
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

export const getArticleCategories = async (): Promise<{ res: string; categories: ArticleCategory[] }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/Api/service/web/get_article_categories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return await response.json();
  } catch (error) {
    console.error('Error fetching article categories:', error);
    return { res: 'ERROR', categories: [] };
  }
};

export const getArticles = async (params?: {
  category_slug?: string;
  page?: number;
}): Promise<{ res: string; articles: Article[]; pagination: ArticlesPagination }> => {
  try {
    const body = new URLSearchParams();
    if (params?.category_slug) body.append('category_slug', params.category_slug);
    if (params?.page) body.append('page', String(params.page));

    const response = await fetch(`${API_BASE_URL}/Api/service/web/get_articles`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    const data = await response.json();

    // ה-API מחזיר total/page/limit ישירות — בונים pagination מקונן
    return {
      res: data.res,
      articles: data.articles || [],
      pagination: {
        total: data.total || 0,
        page: data.page || 1,
        limit: data.limit || 20,
        total_pages: Math.ceil((data.total || 0) / (data.limit || 20)),
      },
    };
  } catch (error) {
    console.error('Error fetching articles:', error);
    return { res: 'ERROR', articles: [], pagination: { total: 0, page: 1, limit: 20, total_pages: 0 } };
  }
};

export const getArticleBySlug = async (slug: string): Promise<{ res: string; article?: ArticleFull }> => {
  try {
    const body = new URLSearchParams({ slug });
    const response = await fetch(`${API_BASE_URL}/Api/service/web/get_article_by_slug`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    return await response.json();
  } catch (error) {
    console.error('Error fetching article:', error);
    return { res: 'ERROR' };
  }
};